import React from 'react'
import type { ConversationType, InboxParticipant } from './inboxTypes'

type ConversationSummaryIconVariant =
  | 'direct-general'
  | 'group-general'
  | 'direct-issue'
  | 'group-issue'

type ConversationSummaryParticipantKind = 'direct' | 'group'
type ConversationSummaryIconKind = 'general' | 'issue'

interface ConversationSummaryIconProps {
  conversationType: ConversationType
  participants: InboxParticipant[]
}

const iconKindByConversationType: Record<ConversationType, ConversationSummaryIconKind> = {
  general: 'general',
  review: 'general',
  issue: 'issue'
}

const variantByParticipantKindAndIconKind: Record<
ConversationSummaryParticipantKind,
Record<ConversationSummaryIconKind, ConversationSummaryIconVariant>
> = {
  direct: {
    general: 'direct-general',
    issue: 'direct-issue'
  },
  group: {
    general: 'group-general',
    issue: 'group-issue'
  }
}

const getConversationSummaryIconVariant = (
  conversationType: ConversationType,
  participantCount: number
): ConversationSummaryIconVariant => {
  const participantKind: ConversationSummaryParticipantKind = participantCount > 1 ? 'group' : 'direct'
  const iconKind = iconKindByConversationType[conversationType]

  return variantByParticipantKindAndIconKind[participantKind][iconKind]
}

const iconByVariant: Record<ConversationSummaryIconVariant, { className: string, name: string }> = {
  'direct-general': { className: 'fa-light fa-user', name: 'user' },
  'group-general': { className: 'fa-kit fa-pro-multi-users', name: 'pro-multi-users' },
  'direct-issue': { className: 'fa-light fa-user', name: 'user' },
  'group-issue': { className: 'fa-kit fa-pro-multi-users', name: 'pro-multi-users' }
}

const labelByVariant: Record<ConversationSummaryIconVariant, string> = {
  'direct-general': 'Direct conversation',
  'group-general': 'Group conversation',
  'direct-issue': 'Direct issue conversation',
  'group-issue': 'Group issue conversation'
}

interface ConversationSummaryBaseIconProps {
  icon: { className: string, name: string }
}

const ConversationSummaryBaseIcon: React.FC<ConversationSummaryBaseIconProps> = ({
  icon
}) => (
  <span className='conversation-list-icon-circle'>
    <i
      className={`${icon.className} conversation-list-icon-image`}
      aria-hidden='true'
      data-testid='conversation-summary-base-icon'
      data-icon-name={icon.name}
    />
  </span>
)

const ConversationSummaryIssueIndicator: React.FC = () => (
  <i
    className='fa-kit fa-pro-comment-exclamation conversation-list-icon-issue-indicator'
    aria-hidden='true'
    data-testid='conversation-summary-issue-indicator'
    data-icon-name='comment-exclamation'
  />
)

export const ConversationSummaryIcon: React.FC<ConversationSummaryIconProps> = ({
  conversationType,
  participants
}) => {
  const participantCount = participants.length
  const variant = getConversationSummaryIconVariant(conversationType, participantCount)
  const isIssue = variant === 'direct-issue' || variant === 'group-issue'
  const icon = iconByVariant[variant]

  return (
    <span
      role='img'
      className='conversation-list-icon'
      aria-label={labelByVariant[variant]}
      data-testid='conversation-summary-icon'
      data-variant={variant}
    >
      <ConversationSummaryBaseIcon icon={icon} />
      {isIssue && <ConversationSummaryIssueIndicator />}
    </span>
  )
}
